import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../../../components/Header";
import AdminContainer from "../../../containers/Admin";
import { routes } from "../../../common/svg";
import { ApiService } from "../../../service/api.service";
import { MASTER_URL } from "../../../common/common";
import RouteMap from "../../../components/RouteMap/RouteMap";

interface RouteDetails {
    routeId: string;
    name: string;
    pickupPoint: string;
    loadingPoint: string;
    unloadingPoint: string;
    clearancePoint: string;
    pickupLatitude: number;
    pickupLongitude: number;
    loadingLatitude: number;
    loadingLongitude?: number;
    unloadingLatitude: number;
    unloadingLongitude?: number;
    clearanceLatitude: number;
    clearanceLongitude: number;
    isActive?: boolean | number;
}

const RouteViewPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const apiService = new ApiService();
    const [routeData, setRouteData] = useState<RouteDetails | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchRouteDetails = async () => {
        try {
            setLoading(true);
            const response: any = await apiService.getApi(
                `${MASTER_URL}/route/get-one-route?routeId=${id}`
            );

            if (response.status === "200" || response.status === 200) {
                setRouteData(response.body);
                setError(null); // Clear any previous error
            } else {
                setError("Route not found");
            }
        } catch (error) {
            console.error("Error fetching route details:", error);
            setError("Failed to load route details");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchRouteDetails();
        }
    }, [id]);

    const handleEdit = () => {
        navigate(`/routes/edit/${id}`);
    };

    const handleBack = () => {
        navigate("/routes");
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-lg">Loading route details...</div>
            </div>
        );
    }

    if (error || !routeData) {
        return (
            <div className="flex flex-col justify-center items-center h-64">
                <div className="text-lg text-red-600 mb-4">{error || "Route not found"}</div>
                <button
                    onClick={handleBack}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                    Back to Routes
                </button>
            </div>
        );
    }

    return (
        <div>
            <Header
                icon={routes}
                title="Route Details"
            >
                <div className="flex gap-2">
                    <button
                        onClick={handleBack}
                        className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                    >
                        Back
                    </button>
                    <button
                        onClick={handleEdit}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        Edit Route
                    </button>
                </div>
            </Header>

            <AdminContainer>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Route Information */}
                    <div className="bg-white p-6 rounded-lg shadow-sm">
                        <h3 className="text-lg font-semibold mb-4">Route Information</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Route Name</label>
                                <p className="mt-1 text-sm text-gray-900">{routeData.name}</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Status</label>
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${(routeData.isActive === true || routeData.isActive === 1)
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                    }`}>
                                    {(routeData.isActive === true || routeData.isActive === 1) ? 'Active' : 'Inactive'}
                                </span>
                            </div>

                        </div>
                    </div>

                    {/* Route Points */}
                    <div className="bg-white p-6 rounded-lg shadow-sm">
                        <h3 className="text-lg font-semibold mb-4">Route Points</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Pickup Point</label>
                                <p className="mt-1 text-sm text-gray-900">{routeData.pickupPoint}</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Loading Point</label>
                                <p className="mt-1 text-sm text-gray-900">{routeData.loadingPoint}</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Clearance Point</label>
                                <p className="mt-1 text-sm text-gray-900">{routeData.clearancePoint}</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Unloading Point</label>
                                <p className="mt-1 text-sm text-gray-900">{routeData.unloadingPoint}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Route Map */}
                <div className="mt-6 bg-white p-6 rounded-lg shadow-sm">
                    <h3 className="text-lg font-semibold mb-4">Route Map</h3>
                    <div className="h-96">
                        <RouteMap
                            pickupPoint={{
                                address: routeData.pickupPoint,
                                lat: routeData.pickupLatitude,
                                lng: routeData.pickupLongitude
                            }}
                            loadingPoint={routeData.loadingLatitude && routeData.loadingLongitude ? {
                                address: routeData.loadingPoint,
                                lat: routeData.loadingLatitude,
                                lng: routeData.loadingLongitude
                            } : undefined}
                            unloadingPoint={routeData.unloadingLatitude ? {
                                address: routeData.unloadingPoint,
                                lat: routeData.unloadingLatitude > 50 ? 12.987454 : routeData.unloadingLatitude, // Use Chennai Airport lat if value looks like longitude
                                lng: routeData.unloadingLongitude || (routeData.unloadingLatitude > 50 ? 80.175349 : routeData.unloadingLatitude) // Use Chennai Airport lng if missing
                            } : undefined}
                            clearancePoint={{
                                address: routeData.clearancePoint,
                                lat: routeData.clearanceLatitude,
                                lng: routeData.clearanceLongitude
                            }}
                        />
                    </div>
                </div>
            </AdminContainer>
        </div>
    );
};

export default RouteViewPage;
